<template>
  <div class="border-top small-margin-top-s small-padding-top-s large-margin-top-m large-padding-top-m hide-for-print">
    <template v-if="rmaData.packageSequence === 1">
      <h5 v-text="title"></h5>
      <p v-if="!returnRequired" class="margin-top-xxs" v-text="RMA_DETAIL.REFUND_RETURN_NOT_REQUIRED.DESCRIPTION"></p>
    </template>
    <p
      v-if="packagesCount[rmaData.packageKey] > 1"
      class="heavy"
      :class="{ 'margin-top-s': rmaData.packageSequence === 1 }"
      v-text="`Package ${rmaData.packageSequence} of ${packagesCount[rmaData.packageKey]}`"
    ></p>
    <p v-if="!isSSRE && returnRequired && rmaData.returnAcceptanceDeadline" class="margin-top-xxs">
      Return by {{ formatDate(rmaData.returnAcceptanceDeadline) }}
    </p>
    <!-- item thumbnail -->
    <div id="product-images" class="grid-x margin-top-m">
      <template v-if="!isSSRE">
        <component
          :is="item.productId ? 'a' : 'div'"
          v-for="(item, index) in rmaData.rmaLines"
          :key="item.productId"
          :href="`/shop/product/?ID=${item.productId}`"
          :title="item.itemDescription"
          style="width: 108px"
          class="product-image-wrapper"
        >
          <div v-if="item.returnQuantity > 1" class="product-qty-container">
            <span class="number-badge qty-circle"></span>
            <span class="product-qty small">{{ item.returnQuantity }}</span>
          </div>
          <Picture
            :image-url="item.urlTemplate && item.primaryImageFilePath && item.urlTemplate + item.primaryImageFilePath"
            :alt="item.itemDescription"
            :aspect-ratio="108 / 136"
            :lazy-load="!(isFirstPackage && index === 0)"
            :fetchpriority="isFirstPackage && index === 0 ? 'high' : 'auto'"
            :fallback-pic-text="`${item.itemDescription.substring(0, 15)}...`"
          />
        </component>
      </template>
      <template v-else>
        <ProductThumbnail
          v-for="(product, index) in rmaData.rmaLines"
          :key="product"
          alignment="horizontal"
          :url-template="{ product: product.urlTemplate }"
          :image-width="100"
          :priority="isFirstPackage && index === 0 ? 'high' : 'auto'"
          :product="{
            detail: {
              name:
                removeBrandFromName(product.brandName, product.itemDescription) || 'There was an error loading the item information. Try again later',
              brand: {
                name: product.brandName || (product.itemDescription ? 'Brand Name' : 'Item Name'),
              },
              colorAndSize: [commaSeparate(product.size, product.color)],
              quantity: product.returnQuantity,
            },
            imagery: {
              urlTemplate: product.urlTemplate,
              primaryImage: {
                filePath: product.primaryImageFilePath,
              },
            },
            identifier: {
              productUrl: product.productId ? `/shop/product/?ID=${product.productId}` : undefined,
            },
          }"
        />
      </template>
    </div>

    <!-- summary -->
    <div class="grid-x margin-top-m">
      <div id="summary-container" class="cell small-12 medium-8 large-6">
        <SummaryRow
          v-for="(row, index) in returnSummary()"
          :id="row.id || row.rowSeq"
          :key="row.rowSeq"
          :row="row"
          :class="row.icon && 'grid-x align-middle'"
          :space-between="!isSSRE"
          :show-margin-top="index > 0"
          is-v3-return-confirmation
        />
      </div>
    </div>

    <!-- return required details -->
    <template v-if="!isSSRE && returnRequired">
      <!-- printlabel -->
      <AppButton
        v-if="showPrintLabel"
        id="print-label"
        ref="printLabelRef"
        class="is-visible margin-top-s primary button expanded cta"
        :loading="isLoading === rmaData.rmaId || isLoading === rmaData.pidToken"
        @click.prevent="printLabel"
      >
        {{ packagesCount[rmaData.packageKey] > 1 ? `Print Label: Package ${rmaData.packageSequence}` : 'Print Return Label' }}
      </AppButton>

      <div class="grid-x align-center">
        <!-- QR CODE -->
        <QRCode
          v-if="hasStoreOrLabelFree"
          class="margin-top-s"
          :qrcode-number="getQRcodeNumber"
          :qrcode-url="rmaData.dropOffUrl"
          :scan-text="getScanText"
          :hide-number="hideQRCodeNumber"
        />

        <div class="cell small-12 medium-auto" :class="{ 'medium-margin-left-s': hasStoreOrLabelFree }">
          <ReturnInstructions
            :return-drop-off-method="rmaData.returnDropOffMethod"
            :refund-method="rmaData.refundMethod"
            :items="rmaData.rmaLines"
            :is-mkp-order="rmaData.isMkpOrder"
            make-ups-clickable
            :is-boris-return="isBorisReturn"
            :has-qr-code-url="!!props.rmaData.dropOffUrl"
            @ups-click="showLocatorSlideout"
          />
          <div v-if="locatorLinkText" class="margin-top-xs">
            <button id="locator-link" class="link-body" @click="showLocatorSlideout" v-text="locatorLinkText"></button>
          </div>
        </div>
      </div>

      <!-- refund drawer -->
      <button v-if="!isSingleRma" type="button" class="is-visible link-body margin-top-xs" @click="showRefundInstructions">
        When will I get my refund?
      </button>
    </template>

    <!-- resend -->
    <Notification
      v-if="
        !isSingleRma && returnConfirmationStore.returnLabel.showNotification && returnConfirmationStore.returnLabel.rmaId === rmaData.rmaId && !isSSRE
      "
      :class="isSingleRma ? 'margin-top-xs' : 'margin-top-m'"
      severity="success"
      :closable="false"
      aria-live="assertive"
    >
      We emailed your return confirmation to
      {{ returnConfirmationStore.returnLabel.emailAddress }}.
    </Notification>
    <button v-if="!isSingleRma" class="margin-top-m link-body" type="button" @click="resendEmailHandler(rmaData.rmaId)">Resend email</button>
  </div>
  <div v-if="isSingleRma && isSSRE" class="border-top margin-top-m padding-top-m padding-bottom-m hide-for-print">
    <h5 v-text="RMA_DETAIL.SSRE.RECEIVING_TITLE"></h5>
    <!-- item thumbnail -->
    <div id="product-images" class="grid-x margin-top-m">
      <ProductThumbnail
        v-for="product in rmaData.substituteLines"
        :key="product"
        alignment="horizontal"
        :url-template="{ product: product.urlTemplate }"
        :image-width="100"
        :product="{
          detail: {
            name:
              removeBrandFromName(product.brandName, product.itemDescription) || 'There was an error loading the item information. Try again later',
            brand: {
              name: product.brandName || (product.itemDescription ? 'Brand Name' : 'Item Name'),
            },
            colorAndSize: [commaSeparate(product.size, product.color)],
            quantity: product.returnQuantity,
          },
          imagery: {
            urlTemplate: product.urlTemplate,
            primaryImage: {
              filePath: product.primaryImageFilePath,
            },
          },
          identifier: {
            productUrl: product.productId ? `/shop/product/?ID=${product.productId}` : undefined,
          },
        }"
      />
    </div>

    <!-- summary -->
    <div class="grid-x margin-top-m">
      <div id="summary-container" class="cell small-12 medium-8 large-6">
        <SummaryRow
          v-for="(row, index) in returnSummary(true)"
          :id="row.id || row.rowSeq"
          :key="row.rowSeq"
          :row="row"
          :class="row.icon && 'grid-x align-middle'"
          :space-between="!isSSRE"
          :show-margin-top="index > 0"
          is-v3-return-confirmation
        />
      </div>
    </div>
  </div>
  <!-- print label -->
  <div v-if="!isSSRE && returnRequired && (isSingleRma || showPrintPreview)" class="print-return-label-confirmation-container">
    <PrintReturnLabel :return-details="enhancedRmaData" />
  </div>
</template>

<script setup>
// Imports
import { computed, markRaw, defineAsyncComponent, ref, onMounted, nextTick } from 'vue';
import { fireTag } from '@vue-util/tagManager';
import ProductThumbnail from '@/features/ProductThumbnail/components/ProductThumbnail';
import Picture from '@nuxt-common/atomic-ui/app/components/picture/Picture.vue';
import Notification from '@atomic-ui/notification/Notification';

// Local components
import SummaryRow from '@/components/common/SummaryRow.vue';
import PrintReturnLabel from '@/components/returns/PrintReturnLabel.vue';
import ReturnInstructions from '@/components/returns/ReturnInstructions.vue';
import QRCode from '@/components/common/QRCode.vue';
import AppButton from '@/components/AppButton.vue';

// Utility Functions and Constants
import { formatDate, commaSeparate, removeBrandFromName } from '@/utils/General';
import { formatPrice, makeNumberAccessible } from '@/utils/returns/General';
import { LOCATOR_LOCATION_RADIUS, LOCATOR_RESULT_LIMIT } from '#shared/constants';

// Config
import { RMA_DETAIL, LOCATOR_TITLE, RETURN_METHOD_KEY_MAP, LOCATOR_LINK_TEXT, SCAN_QR_TEXT, RETURN_RESOLUTIONS, REFUND_METHOD } from 'config';

// Stores & Composables
import { useReturnItemStore } from '@/stores/returns/useReturnItemStore';
import { useSlideout } from '@/composables/useSlideout';
import { useReturnConfirmationStore } from '@/stores/returns/useReturnConfirmationStore';

const returnItemsStore = useReturnItemStore();
const returnConfirmationStore = useReturnConfirmationStore();

const envPropsStore = useEnvPropsStore();
const { registerSlideout, toggleSlideout } = useSlideout();
const { registerModal, toggleModal } = useModal();

const printLabelRef = ref(null);
const showPrintPreview = ref(false);

const props = defineProps({
  rmaData: {
    type: Object,
    required: true,
  },
  packagesCount: {
    type: Object,
    required: true,
  },
  isSingleRma: {
    type: Boolean,
    required: true,
  },
  isBorisReturn: {
    type: Boolean,
    required: true,
  },
  isFirstPackage: {
    type: Boolean,
    default: false,
  },
});

const isLoading = computed(() => returnItemsStore.loading?.getShippingDetails);
const isError = computed(() => returnItemsStore.errors?.getShippingDetails);
const shippingDetails = computed(() => returnItemsStore.shippingDetails);

const enhancedRmaData = computed(() => {
  const shippingLabels = shippingDetails.value?.shippingLabels ?? [];
  const shippingLabel = shippingLabels[0] ?? {};

  const items = props.rmaData.rmaLines.map((rmaLine) => ({
    upc: rmaLine.upc,
    returnQuantity: rmaLine.returnQuantity,
    currentPrice: rmaLine.unitPrice,
    itemTotal: rmaLine.itemTotal,
    itemDetails: {
      productId: rmaLine.productId,
      primaryImageFilePath: rmaLine.primaryImageFilePath,
      urlTemplate: rmaLine.urlTemplate,
      productName: rmaLine.itemDescription,
      upcNumber: rmaLine.upc,
      upcColor: rmaLine.color,
      upcSize: rmaLine.size,
      quantity: rmaLine.returnQuantity,
      hazmatDescription: rmaLine.hazmatDescription,
      vendorName: rmaLine.vendorName,
      mkpLineItemDetails: rmaLine.mkpLineItemDetails || {
        sellerName: rmaLine.sellerName,
        partnerFulfillmentId: rmaLine.partnerFulfillmentId,
      },
    },
    isHazmatItem: rmaLine.isHazmatItem,
  }));

  return {
    ...props.rmaData,
    emailAddress: props.rmaData.returnEmail,
    pendingReturnId: props.rmaData.rmaId,
    labelImageUrl: props.rmaData.isDcrRedesignEnabled ? props.rmaData.labelImageUrl : shippingLabel?.base64Label,
    returnTrackingNumber: props.rmaData.isDcrRedesignEnabled ? props.rmaData.returnTrackingNumber : shippingLabel?.trackingNumber,
    items,
  };
});

const showPrintLabel = computed(
  () => props.rmaData.returnDropOffMethod === RETURN_METHOD_KEY_MAP.ups || props.rmaData.returnDropOffMethod === RETURN_METHOD_KEY_MAP.fedex,
);

const locatorLinkText = computed(() => {
  const linkText = LOCATOR_LINK_TEXT.config(props.isBorisReturn)[props.rmaData.returnDropOffMethod];
  if (props.rmaData.returnDropOffMethod === RETURN_METHOD_KEY_MAP.storeReturn) return linkText;

  return '';
});

const isSSRE = computed(() => [RETURN_RESOLUTIONS.REPLACEMENT, RETURN_RESOLUTIONS.EXCHANGE].includes(props.rmaData.returnResolution));

const hideQRCodeNumber = computed(
  () =>
    props.rmaData.returnDropOffMethod === RETURN_METHOD_KEY_MAP.storeReturn ||
    (props.rmaData.returnDropOffMethod === RETURN_METHOD_KEY_MAP.labelFree && props.rmaData.dropOffUrl),
);

const hasStoreOrLabelFree = computed(() => {
  // Show QRCODE for 3P STORE based on dropOffUrl
  if (props.isBorisReturn) return !!props.rmaData.dropOffUrl;
  // SHOW QRCODE for 1P STORE and 1P/3P label-free
  return [RETURN_METHOD_KEY_MAP.labelFree, RETURN_METHOD_KEY_MAP.storeReturn].includes(props.rmaData.returnDropOffMethod);
});

const getQRcodeNumber = computed(() => {
  if (props.isBorisReturn) return props.rmaData.orderNumber;
  if (props.rmaData.returnDropOffMethod === RETURN_METHOD_KEY_MAP.labelFree)
    return props.rmaData.isDcrResponse ? props.rmaData.labelFreeReturnTrackingNumber : props.rmaData.returnTrackingNumber;
  if (props.rmaData.returnDropOffMethod === RETURN_METHOD_KEY_MAP.storeReturn) return props.rmaData.rmaId;
  return '';
});

const getScanText = computed(() => SCAN_QR_TEXT.config(props.isBorisReturn)[props.rmaData.returnDropOffMethod]);

const returnRequired = computed(() => props.rmaData.rmaType === RMA_DETAIL.RETURN_REQUIRED.TYPE);

const RefundInstructions =
  returnRequired.value &&
  markRaw(defineAsyncComponent(() => import(/* webpackChunkName: "refund-instructions-slideout" */ '@/components/returns/RefundInfo.vue')));

const title = computed(() => {
  if (isSSRE.value) return RMA_DETAIL.SSRE.RETURNING_TITLE;
  if (props.rmaData.returnDropOffMethod === RETURN_METHOD_KEY_MAP.storeReturn) {
    return RMA_DETAIL.RETURN_ITEM.TITLE;
  }
  if (returnRequired.value) {
    return RMA_DETAIL.RETURN_REQUIRED.TITLE;
  }
  return RMA_DETAIL.REFUND_RETURN_NOT_REQUIRED.TITLE;
});

const returnSummary = (isSubstituteItem = false) => {
  const addRow = (condition, row) => (condition ? [row] : []);
  let rowOne;
  if (isSSRE.value) {
    rowOne = {
      label: isSubstituteItem ? 'New order total' : 'Original order total',
      value: isSubstituteItem ? formatPrice(props.rmaData.substituteEstimatedTotal) : formatPrice(props.rmaData.estimatedRefund * -1),
    };
  } else {
    rowOne = {
      label: 'Estimated refund',
      value: formatPrice(props.rmaData.estimatedRefund),
    };
  }
  const rows = [
    {
      rowSeq: 1,
      label: rowOne.label,
      labelClass: 'medium',
      value: rowOne.value,
      valueClass: 'heavy',
    },
    ...addRow(props.rmaData.orderNumber, {
      rowSeq: 2,
      label: 'Order #',
      labelClass: 'medium',
      value: props.rmaData.orderNumber,
      screenReaderValue: makeNumberAccessible(props.rmaData.orderNumber),
    }),
    ...addRow(!isSubstituteItem && !props.isBorisReturn && props.rmaData.rmaId, {
      rowSeq: 3,
      label: 'Return ID',
      labelClass: 'medium',
      value: props.rmaData.rmaId,
      screenReaderValue: makeNumberAccessible(props.rmaData.rmaId),
    }),
  ];

  return rows;
};

const printLabel = async () => {
  showPrintPreview.value = false;
  await nextTick();
  showPrintPreview.value = true;
  await nextTick();
  fireTag('link', {
    link_name: 'print return label',
    event_name: 'print return label',
  });

  if (props.rmaData.pidToken) {
    await returnItemsStore.GET_SHIPPING_DETAILS({
      pidToken: props.rmaData.pidToken,
    });
    printLabelRef.value.$el.focus();
    await nextTick();
    if (!isLoading.value && !isError.value) window.print();
  } else if (props.rmaData.rmaId && !props.rmaData.isDcrResponse) {
    await returnItemsStore.GET_SHIPPING_DETAILS_BY_RMA_ID({
      rmaId: props.rmaData.rmaId,
    });
    printLabelRef.value.$el.focus();
    await nextTick();
    if (!isLoading.value && !isError.value) window.print();
  } else window.print();
};

function showLocatorSlideout(event) {
  const rmaContactAddress = props.rmaData.contact?.address || {};
  const address = [
    rmaContactAddress.addressLine1,
    rmaContactAddress.addressLine2,
    rmaContactAddress.addressLine3,
    rmaContactAddress.city,
    rmaContactAddress.state,
    rmaContactAddress.postalCode,
  ].reduce((acc, field) => {
    if (!field) return acc;
    if (acc) return `${acc}, ${field}`;
    return field;
  }, '');

  registerSlideout({
    id: 'return-confirmation-locator-slideout',
    title: LOCATOR_TITLE[props.rmaData.returnDropOffMethod],
    elementIdToFocus: event?.target.id,
    props: {
      typeOf: props.rmaData.returnDropOffMethod,
      address,
      newGoogleApiCall: props.rmaData.isNewGoogleAPICallEnabled,
      useMspLocatorApi: true,
      locationRadius: LOCATOR_LOCATION_RADIUS,
      resultsDisplayed: LOCATOR_RESULT_LIMIT,
      showMyStoreCTA: false,
      separateOutlets: true,
      page: 'returns',
      excludeGob: true,
      storeInPreferenceDatabaseEnabled: props.rmaData.storeInPreferenceDatabaseEnabled,
      newPlacesDetailsApiEnabled: props.rmaData.newPlacesDetailsApiEnabled,
      commonLocationServiceApiKey: envPropsStore.envProps.xMacysApiKey,
    },
    component: markRaw(defineAsyncComponent(() => import(/* webpackChunkName: "locator-slideout" */ '@nuxt-common/locator/app/components/Locator'))),
  });
  toggleSlideout('return-confirmation-locator-slideout');
}

function showRefundInstructions(event) {
  registerSlideout({
    id: 'return-confirmation-refund-instructions',
    title: 'When will I get my refund?',
    elementIdToFocus: event?.target.id,
    props: {
      returnDetails: props.rmaData,
    },
    component: RefundInstructions,
  });
  toggleSlideout('return-confirmation-refund-instructions');
}

async function resendEmailHandler(rmaId) {
  registerModal({
    id: 'return-label',
    title: 'Where should we send your return confirmation?',
    width: '628px',
    component: markRaw(defineAsyncComponent(() => import('@/components/returns/GetReturnLabel'))),
    props: {
      isReturnConfirmation: true,
      rmaId: rmaId,
    },
  });
  toggleModal('return-label');
}

const fireTealiumTagOnPageLoad = () => {
  const { returnResolution, orderNumber, returnDropOffMethod, substituteLines, isMkpOrder, estimatedRefund, refundMethod } = props.rmaData;

  const isLabelFreeReturn = props.rmaData.returnDropOffMethod === RETURN_METHOD_KEY_MAP.labelFree ? 'true' : 'false';

  const productIds = [];
  const returnedQuantities = [];
  const reasonsForReturn = [];
  const itemTotal = [];
  const productColors = [];
  const productSizes = [];
  const protectionPlanPurchased = [];
  const protectionPlanReturnAmount = [];

  const linesKey = substituteLines ? 'substituteLines' : 'rmaLines';
  props.rmaData[linesKey].forEach((line) => {
    productIds.push(line.productId || '');
    returnedQuantities.push(line.returnQuantity || 0);
    reasonsForReturn.push(line.reasonDescription || '');
    itemTotal.push(line.itemTotal || '');
    productSizes.push(line.size || '');
    productColors.push(line.color || '');
    protectionPlanPurchased.push(line.planDesc || '');
    protectionPlanReturnAmount.push(line.protectionPlanPrice?.toString()?.replace(/\$/g, '') * line.returnQuantity?.toString() || '');
  });

  const pageName = isSSRE.value ? returnResolution?.toLowerCase() : 'return';
  const data = {
    page_name: `${pageName} confirmation`,
    page_type: 'order history',
    action_location: 'order details',
    product_id: productIds,
    reason_for_return: reasonsForReturn,
    return_method: returnDropOffMethod,
    label_free_return: isLabelFreeReturn,
    marketPlaceItem: isMkpOrder ? 'true' : 'false',
    protection_plan_purchased: protectionPlanPurchased,
    protection_plan_return_amount: protectionPlanReturnAmount,
  };

  const isStoreCredit = refundMethod === REFUND_METHOD.WALLET;

  const returnProperties = {
    site_action: 'submit returns',
    quantity_returned: returnedQuantities,
    return_product_prices: itemTotal,
    return_refund_amount: estimatedRefund,
    refund_type: isStoreCredit ? 'store credit' : refundMethod,
  };

  const ssreProperties = {
    return_order_id: orderNumber,
    product_color: productColors,
    product_size: productSizes,
    product_price: itemTotal,
  };

  let eventData = {};
  if ([RETURN_RESOLUTIONS.EXCHANGE, RETURN_RESOLUTIONS.REPLACEMENT].includes(returnResolution)) eventData = { ...data, ...ssreProperties };
  else eventData = { ...data, ...returnProperties };
  fireTag('view', eventData);
};

onMounted(() => {
  fireTealiumTagOnPageLoad();
});
</script>

<style lang="scss">
#product-images {
  gap: rem-calc(24);
  .product-image {
    flex-shrink: 0;
  }
}

.print-return-label-confirmation-container {
  display: none;
}
@media print {
  #nav-header-wrapper,
  #nav-header,
  #nav-footer {
    display: none;
  }

  section .banner-content {
    display: none !important;
  }

  .print-return-label-confirmation-container {
    display: block;
    position: relative;
    overflow-y: visible;
  }
}
</style>

<style lang="scss" scoped>
@import '~@core/csg/src/scss/encodedIcons/mcom/_number-badge.scss';

.product-image-wrapper {
  position: relative;
  .product-qty-container {
    position: absolute;
    top: rem-calc(-10);
    right: rem-calc(-10);
    z-index: 1;
    .product-qty {
      position: absolute;
      right: rem-calc(8);
      top: rem-calc(2);
    }
  }
}
</style>
